import { Route, Routes } from 'react-router-dom';
import { routePaths } from './routes';
import { ProtectedRoute } from './guards/ProtectedRoute';
import { PublicOnlyRoute } from './guards/PublicOnlyRoute';
import { SectionGate } from './guards/SectionGate';
import { NotFoundPage } from './NotFoundPage';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { AuthCallbackPage } from '@/features/auth/pages/AuthCallbackPage';
import { AuthStartPage } from '@/features/auth/pages/AuthStartPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { VerifyEmailPage } from '@/features/auth/pages/VerifyEmailPage';
import { HomePage } from '@/features/dashboard/pages/HomePage';
import { AppointmentsPage } from '@/features/appointments/pages/AppointmentsPage';
import { NewAppointmentPage } from '@/features/appointments/pages/NewAppointmentPage';
import { ResultsPage } from '@/features/results/pages/ResultsPage';
import { ResultDetailPage } from '@/features/results/pages/ResultDetailPage';
import { PatientsPage } from '@/features/patients/pages/PatientsPage';
import { AddPatientPage } from '@/features/patients/pages/AddPatientPage';
import { AttachedAccountsPage } from '@/features/account/pages/AttachedAccountsPage';

/** Declarative route tree, split by access level via guard layouts. */
export function AppRoutes() {
  return (
    <Routes>
      {/* OAuth redirect target — ungated (runs before tokens exist). */}
      <Route path={routePaths.authCallback} element={<AuthCallbackPage />} />
      {/* Neutral session-start entry — forwards a one-time ticket to auth. */}
      <Route path={routePaths.authStart} element={<AuthStartPage />} />

      {/* Auth screens — only for signed-out visitors. */}
      <Route element={<PublicOnlyRoute />}>
        <Route path={routePaths.login} element={<LoginPage />} />
        <Route path={routePaths.register} element={<RegisterPage />} />
        <Route path={routePaths.forgotPassword} element={<ForgotPasswordPage />} />
      </Route>

      {/* Authenticated app — wrapped in the shared shell. */}
      <Route element={<ProtectedRoute />}>
        {/* Post-login email verification, outside the shell: ProtectedRoute
            sends unverified accounts here and keeps everything else closed. */}
        <Route path={routePaths.verifyEmail} element={<VerifyEmailPage />} />
        <Route element={<AppLayout />}>
          <Route path={routePaths.home} element={<HomePage />} />
          {/* Sections that the admin portal can put in maintenance independently. */}
          <Route element={<SectionGate section="appointments" />}>
            <Route path={routePaths.appointments} element={<AppointmentsPage />} />
            <Route path={routePaths.newAppointment} element={<NewAppointmentPage />} />
          </Route>
          <Route element={<SectionGate section="medical_record" />}>
            <Route path={routePaths.results} element={<ResultsPage />} />
            <Route path={`${routePaths.results}/:consId`} element={<ResultDetailPage />} />
          </Route>
          <Route path={routePaths.patients} element={<PatientsPage />} />
          <Route path={routePaths.addPatient} element={<AddPatientPage />} />
          <Route path={routePaths.attachedAccounts} element={<AttachedAccountsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
